// Lookup: AccessoryId → React component.
//
// Adding a new primitive:
//   1. Add the AccessoryId union member in tribeVisualSpec.ts
//   2. Build the component in primitives/
//   3. Register it here
//   4. (Optional) assign it to a tribe in TRIBE_VISUAL_SPECS

import type { ComponentType } from 'react';
import type { AccessoryId } from './tribeVisualSpec';
import { Spraycan } from './primitives/Spraycan';
import { HydraulicSwitch } from './primitives/HydraulicSwitch';
import { StuddedBelt } from './primitives/StuddedBelt';
import { PaleteroBell } from './primitives/PaleteroBell';
import { MicStand } from './primitives/MicStand';
import { ClipboardKeys } from './primitives/ClipboardKeys';
import { PendletonPattern } from './primitives/PendletonPattern';
import { HoopEarrings } from './primitives/HoopEarrings';
import { TejanaBrim } from './primitives/TejanaBrim';
import { ChromeRim } from './primitives/ChromeRim';
import { GuitarStrat } from './primitives/GuitarStrat';
import { TaqueroTongs } from './primitives/TaqueroTongs';
import { FruteroUmbrella } from './primitives/FruteroUmbrella';
import { EloteroPot } from './primitives/EloteroPot';

export interface AccessoryProps {
  bodyColor?: string;
  accentColor?: string;
  size?: number;
  className?: string;
}

/**
 * Registry of accessory primitives. `null` entries are placeholders —
 * the spec lists them but the component is not yet built, so CardTile
 * falls back to no-accessory rendering for cards whose tribe references
 * an unbuilt primitive.
 */
export const ACCESSORY_REGISTRY: Record<AccessoryId, ComponentType<AccessoryProps> | null> = {
  // === Built — primary tribe accessories (all 6 tribes covered) ===
  'spraycan': Spraycan,
  'hydraulic-switch': HydraulicSwitch,
  'studded-belt': StuddedBelt,
  'paletero-bell': PaleteroBell,
  'mic-stand': MicStand,
  'clipboard-keys': ClipboardKeys,
  // === Built — secondary / tier-3+ variants ===
  'pendleton-pattern': PendletonPattern,
  'hoop-earrings': HoopEarrings,
  'tejana-brim': TejanaBrim,
  'chrome-rim': ChromeRim,
  // === Built — La Tocada + food-vendor secondaries ===
  'guitar-strat': GuitarStrat,
  'taquero-tongs': TaqueroTongs,
  'frutero-umbrella': FruteroUmbrella,
  'elotero-pot': EloteroPot,
};

/** Returns the component for an accessory id, or null if not yet built. */
export function getAccessoryComponent(id: AccessoryId): ComponentType<AccessoryProps> | null {
  return ACCESSORY_REGISTRY[id] ?? null;
}
